import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { History, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FIELD_LABELS = {
  labor_rate: "Tariffa oraria preventivi",
  vat_pct: "IVA",
  distributor_hourly_rate: "Distributore · 1 ora",
  distributor_carnet_10h_price: "Distributore · Carnet 10h",
  end_customer_hourly_rate: "Cliente finale · 1 ora",
  end_customer_carnet_10h_price: "Cliente finale · Carnet 10h",
};

const fmtVal = (field, v) => (v == null ? "—" : field === "vat_pct" ? `${v}%` : `€${Number(v).toFixed(2)}`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");

export function PriceHistoryCard({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/price-history", { params: { limit: 30 } });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <Card className="rounded-sm border-zinc-200 bg-white p-6" data-testid="price-history-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-sm bg-brand-muted text-brand flex items-center justify-center">
          <History className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-900">Storico prezzi</h3>
          <p className="text-sm text-zinc-600">Chi ha modificato tariffe orarie e carnet, e quando.</p>
        </div>
        <Button data-testid="price-history-refresh" variant="outline" size="sm" className="rounded-sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="price-history-empty">Nessuna modifica registrata finora.</p>
      ) : (
        <ol className="relative border-l border-zinc-200 ml-2 space-y-4" data-testid="price-history-list">
          {rows.map((r) => (
            <li key={r.id} className="pl-5 relative" data-testid="price-history-item">
              <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-brand" />
              <div className="text-xs text-zinc-500">{fmtDate(r.changed_at)} · <span className="text-zinc-800 font-medium">{r.user_name}</span></div>
              <ul className="mt-1 space-y-0.5">
                {r.changes.map((c) => (
                  <li key={c.field} className="text-sm text-zinc-800">
                    <span className="text-zinc-600">{FIELD_LABELS[c.field] || c.field}:</span>{" "}
                    <span className="line-through text-zinc-400">{fmtVal(c.field, c.old)}</span>
                    <span className="mx-1.5 text-zinc-400">→</span>
                    <span className="font-semibold text-brand">{fmtVal(c.field, c.new)}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
